import { userRoles } from "../helpers/utility";
import { newRole } from "../domain/clinicalForms";

const CLINICAL_PRACTITIONER_ROLE = "Clinical_Practitioner";

export const BUILTIN_CLINICAL_ROLES = userRoles.filter(
  (role) => role.hod && role.value !== CLINICAL_PRACTITIONER_ROLE
);

export const slugify = (name) =>
  (name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .slice(0, 50)
    .replace(/^_/, "")
    .replace(/_$/, "");

// Reserved codes/names that identify the fixed IA and Review slots. Their
// is_repeatable state is fixed by the platform (IA: false, Review: true)
// and cannot be toggled by admins.
const IA_CODE = "initial_assessment";
const REVIEW_CODES = new Set(["review_form", "review"]);

export const isIaForm = (form) => {
  if (!form) return false;
  if (form.code === IA_CODE) return true;
  return slugify(form.name) === IA_CODE;
};

export const isReviewForm = (form) => {
  if (!form) return false;
  if (REVIEW_CODES.has(form.code)) return true;
  return REVIEW_CODES.has(slugify(form.name));
};

export const toCreateRolePayload = ({ name, description = "" }) => ({
  name,
  code: slugify(name),
  description,
  is_active: true,
});

export const toClinicalRole = (apiRole) => ({
  ...newRole(apiRole.name),
  id: String(apiRole.id),
  code: apiRole.code,
  description: apiRole.description,
  is_active: apiRole.is_active,
});

export const toClinicalRoles = (apiRoles) =>
  (apiRoles || []).map(toClinicalRole);

const roleMatchKeys = (userRole) =>
  new Set([
    slugify(userRole.value),
    slugify(userRole.label),
    userRole.value.toLowerCase(),
  ]);

const matchesBuiltin = (apiRole, userRole) => {
  const keys = roleMatchKeys(userRole);
  return (
    keys.has(apiRole.code) ||
    keys.has(slugify(apiRole.name)) ||
    apiRole.name === userRole.label
  );
};

const isClinicalPractitionerRole = (role) =>
  role?.value === CLINICAL_PRACTITIONER_ROLE ||
  slugify(role?.code) === slugify(CLINICAL_PRACTITIONER_ROLE) ||
  slugify(role?.name) === slugify("Clinical Practitioner");

export const toBuiltinClinicalRole = (userRole) => {
  const role = newRole(userRole.label);
  return {
    ...role,
    id: `builtin_${userRole.value}`,
    code: slugify(userRole.value),
    value: userRole.value,
    system: true,
    is_active: true,
    description: "",
  };
};

export const mergeBuiltinAndApiRoles = (apiRoles = [], overrides = {}) => {
  const matchedApiIds = new Set();

  const merged = BUILTIN_CLINICAL_ROLES.map((userRole) => {
    const builtin = toBuiltinClinicalRole(userRole);
    const override = overrides[builtin.id];
    const fromApi = (apiRoles || []).find((apiRole) => {
      if (matchesBuiltin(apiRole, userRole)) {
        matchedApiIds.add(apiRole.id);
        return true;
      }
      return false;
    });

    if (fromApi) {
      return {
        ...builtin,
        ...fromApi,
        id: fromApi.id,
        name: userRole.label,
        value: userRole.value,
        system: true,
        forms: fromApi.forms?.length ? fromApi.forms : builtin.forms,
        ...(override || {}),
      };
    }

    return override ? { ...builtin, ...override } : builtin;
  });

  const custom = (apiRoles || [])
    .filter((apiRole) => !matchedApiIds.has(apiRole.id))
    .filter((apiRole) => !isClinicalPractitionerRole(apiRole))
    .map((apiRole) =>
      overrides[apiRole.id] ? { ...apiRole, ...overrides[apiRole.id] } : apiRole
    );

  return [...merged, ...custom].filter((role) => !isClinicalPractitionerRole(role));
};

export const toCreateFormPayload = ({
  name,
  code,
  mode,
  schema,
  description,
  isActive,
  isRepeatable,
  clinicalRoleId,
  builtinRole,
}) => {
  const payload = {
    name,
    code,
    encounter_type: mode,
    role: builtinRole ?? "",
    clinical_role: clinicalRoleId ?? "",
  };
  if (schema !== undefined) payload.schema = JSON.stringify(schema);
  if (description !== undefined) payload.description = description;
  if (isActive !== undefined) payload.is_active = isActive;
  if (isRepeatable !== undefined) payload.is_repeatable = isRepeatable;
  return payload;
};

export const toUpdateFormPayload = ({
  name,
  code,
  description,
  isActive,
  isRepeatable,
  mode,
  schema,
  clinicalRoleId,
}) => {
  const payload = {};
  if (name !== undefined) payload.name = name;
  if (code !== undefined) payload.code = code;
  if (description !== undefined) payload.description = description;
  if (isActive !== undefined) payload.is_active = isActive;
  if (isRepeatable !== undefined) payload.is_repeatable = isRepeatable;
  if (mode !== undefined) payload.encounter_type = mode;
  if (schema !== undefined) payload.schema = JSON.stringify(schema);
  if (clinicalRoleId !== undefined) payload.clinical_role = clinicalRoleId;
  return payload;
};

export const toClinicalForms = (apiForms) =>
  (apiForms || []).map(fromApiForm);

export const fromApiForm = (apiForm) => {
  const parsedSchema = safeParseSchema(apiForm.schema);
  return {
    id: String(apiForm.id),
    name: apiForm.name,
    code: apiForm.code,
    mode: apiForm.encounter_type,
    active: apiForm.is_active,
    repeatable: apiForm.is_repeatable,
    description: apiForm.description,
    version: parsedSchema?.version ?? apiForm.version,
    sections: parsedSchema?.sections || [],
  };
};

const safeParseSchema = (raw) => {
  if (!raw) return null;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
